import {
  createContext,
  useCallback,
  useContext,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { ScreenerTokensParams } from "@api/token-screener/screener-tokens.ts";
import { useSearchParamsHandler } from "@/hooks/use-search-params-handler.ts";
import { useTokenScreenerState } from "@/hooks/use-token-screener-state.ts";
import { TokenDropdownValue } from "@pages/token-screener/token-screener-header.tsx";

export const getTokenScreenerDefaultFiltersBySelection = (
  selection: string,
): ScreenerTokensParams["filters"] => {
  switch (selection) {
    case "all":
      return {
        makers: { min: 10 },
        holders: {
          min: 100,
        },
        volume: { min: 1000 },
        liquidity: { min: 1000 },
        sort: {
          marketCap: "desc",
        },
      };
    case "recent":
      return {
        sort: {
          age: "desc",
        },
      };
    case "trending":
      return {
        sort: {
          trending: true,
          priceChange24Hours: "desc",
        },
      };
  }
};

const TokenScreenerFilterContext = createContext<{
  filters: ScreenerTokensParams["filters"];
  onFiltersUpdate: Dispatch<SetStateAction<ScreenerTokensParams["filters"]>>;
  phrase?: string | null;
  onPhraseUpdate: (phrase: string | undefined) => void;
  selection: {
    dropdownValue: TokenDropdownValue;
    showStarred: boolean;
  };
  setDropdown: (val: TokenDropdownValue) => void;
  toggleStarred: () => void;
}>({
  filters: getTokenScreenerDefaultFiltersBySelection("all"),
  onFiltersUpdate: () => {},
  phrase: null,
  onPhraseUpdate: () => {},
  selection: {
    dropdownValue: "all",
    showStarred: false,
  },
  setDropdown: () => {},
  toggleStarred: () => {},
});

export const useTokenScreenerFilter = () => {
  const context = useContext(TokenScreenerFilterContext);
  if (!context) {
    throw new Error(
      "useTokenScreener must be used within TokenScreenerFilterProvider",
    );
  }
  return context;
};

const TokenScreenerFilterProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [filters, setFilters] = useState<ScreenerTokensParams["filters"]>(
    getTokenScreenerDefaultFiltersBySelection("all"),
  );

  const { inputValue: phrase, setInputValue: setPhrase } =
    useSearchParamsHandler().useDebouncedParam("phrase", 500);

  const onPhraseUpdate = useCallback((phrase: string | undefined | null) => {
    setPhrase(phrase ?? "");
  }, []);

  const { selection, setDropdown, toggleStarred } = useTokenScreenerState();

  return (
    <TokenScreenerFilterContext.Provider
      value={{
        filters,
        onFiltersUpdate: setFilters,
        phrase,
        onPhraseUpdate: onPhraseUpdate,
        selection,
        setDropdown,
        toggleStarred,
      }}
    >
      {children}
    </TokenScreenerFilterContext.Provider>
  );
};

export default TokenScreenerFilterProvider;
